import type { HealthRedactionView } from '@steado/admin-contract/user-detail';
import { useMutation } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';

import { Field, Fields, Panel } from '@/components/panel/panels';
import { formatDateTime, humanise } from '@/lib/format';
import { revealHealth } from './user-detail-query';

interface HealthPanelProps {
  readonly userId: string;
  readonly redaction: HealthRedactionView;
}

/**
 * Medical conditions, medications and allergies.
 *
 * The values are not on this page. The detail response carries counts and
 * nothing else, so what renders below the redaction bar is genuinely all the
 * browser has — there is no local flag that would reveal it, and opening dev
 * tools would show the same counts. Pressing Reveal is a request to a separate
 * endpoint that requires role `admin` and writes an audit row before answering.
 *
 * This is the deliberate divergence from the imported design, which reveals by
 * flipping local state over data it already downloaded (design spec §5.2).
 */
export function HealthPanel({ userId, redaction }: HealthPanelProps) {
  const { api, can } = useAuth();

  const reveal = useMutation({
    mutationFn: () => revealHealth(api, userId),
    // Not cached, and never retried: each attempt is an audited event, and a
    // silent retry would log a reveal the operator did not ask for twice.
    retry: false,
  });

  const revealed = reveal.data;

  if (!redaction.recorded) {
    return (
      <Panel title="Health">
        <p className="text-sm leading-relaxed text-muted-foreground">
          No health information recorded. This user has not answered the health questions.
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Health"
      action={
        revealed === undefined ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              reveal.mutate();
            }}
            disabled={!can.revealHealth || reveal.isPending}
            title={
              can.revealHealth
                ? undefined
                : 'Revealing health data requires the admin role. Support operators cannot.'
            }
          >
            {reveal.isPending ? 'Revealing…' : 'Reveal health data'}
          </Button>
        ) : (
          <Badge tone="warn">revealed &amp; logged</Badge>
        )
      }
    >
      {revealed === undefined ? (
        <div className="flex flex-col gap-5">
          <Fields>
            <Field label="Conditions" value={<Redacted count={redaction.conditionCount} />} />
            <Field label="Medications" value={<Redacted count={redaction.medicationCount} />} />
            <Field label="Allergies" value={<Redacted count={redaction.allergyCount} />} />
          </Fields>

          <p className="text-[13px] leading-relaxed text-muted-foreground">
            These values are not sent to the console until they are asked for. Revealing them is recorded
            against your account.
          </p>

          {!can.revealHealth ? (
            <p role="note" className="text-[13px] leading-relaxed text-muted-foreground">
              Your role is read-only for health data. Ask an admin if you need it.
            </p>
          ) : null}

          {reveal.isError ? (
            <p role="alert" className="text-[13px] leading-relaxed text-err-fg">
              {reveal.error.message}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <Fields>
            <Field
              label="Conditions"
              value={
                revealed.conditions.length === 0 ? null : (
                  <span>{revealed.conditions.map(humanise).join(', ')}</span>
                )
              }
            />
            <Field label="Medications" value={revealed.medications} />
            <Field
              label="Allergies"
              value={
                revealed.allergies.length === 0 ? null : (
                  <span>{revealed.allergies.map(humanise).join(', ')}</span>
                )
              }
            />
            <Field label="Tracks cycle" value={revealed.tracksCycle ? 'yes' : 'no'} />
            <Field label="Pregnant or nursing" value={revealed.pregnantOrNursing ? 'yes' : 'no'} />
          </Fields>

          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Revealed {formatDateTime(revealed.revealedAt)} and written to the audit log with your account and
            IP.
          </p>
        </div>
      )}
    </Panel>
  );
}

/**
 * The count is the whole of what the browser knows. Announced as text rather
 * than left as decorative dots, so the placeholder says what it is hiding.
 */
function Redacted({ count }: { readonly count: number }) {
  if (count === 0) return <span className="text-muted-foreground">none recorded</span>;

  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className="font-mono tracking-[0.12em] text-muted-foreground">
        ••••••
      </span>
      <span className="text-[13px] text-muted-foreground">
        {count === 1 ? '1 redacted' : `${String(count)} redacted`}
      </span>
    </span>
  );
}
